import { useState, useCallback, useMemo } from 'react'

export type ValidationRule = {
  validate: (value: any) => boolean
  message: string
}

export type FieldValidation = {
  required?: boolean | string
  minLength?: { value: number; message?: string }
  maxLength?: { value: number; message?: string }
  min?: { value: number; message?: string }
  max?: { value: number; message?: string }
  pattern?: { value: RegExp; message?: string }
  url?: boolean | string
  email?: boolean | string
  custom?: ValidationRule[]
}

export type ValidationSchema<T> = {
  [K in keyof T]?: FieldValidation
}

export type FieldError = {
  hasError: boolean
  message: string
}

export type FormErrors<T> = {
  [K in keyof T]?: FieldError
}

export type TouchedFields<T> = {
  [K in keyof T]?: boolean
}

// URL validation regex
const URL_REGEX = /^https?:\/\/.+/i

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function validateField(value: any, rules: FieldValidation): FieldError {
  // Required validation
  if (rules.required) {
    const isEmpty = value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)
    if (isEmpty) {
      const message = typeof rules.required === 'string' ? rules.required : 'Este campo es obligatorio'
      return { hasError: true, message }
    }
  }

  // Skip other validations if value is empty and not required
  if (value === undefined || value === null || value === '') {
    return { hasError: false, message: '' }
  }

  // String validations
  if (typeof value === 'string') {
    // Min length
    if (rules.minLength && value.length < rules.minLength.value) {
      return {
        hasError: true,
        message: rules.minLength.message || `Mínimo ${rules.minLength.value} caracteres`
      }
    }

    // Max length
    if (rules.maxLength && value.length > rules.maxLength.value) {
      return {
        hasError: true,
        message: rules.maxLength.message || `Máximo ${rules.maxLength.value} caracteres`
      }
    }

    // Pattern
    if (rules.pattern && !rules.pattern.value.test(value)) {
      return {
        hasError: true,
        message: rules.pattern.message || 'Formato no válido'
      }
    }

    // URL validation
    if (rules.url && !URL_REGEX.test(value)) {
      const message = typeof rules.url === 'string' ? rules.url : 'URL no válida (debe comenzar con http:// o https://)'
      return { hasError: true, message }
    }

    // Email validation
    if (rules.email && !EMAIL_REGEX.test(value)) {
      const message = typeof rules.email === 'string' ? rules.email : 'Email no válido'
      return { hasError: true, message }
    }
  }

  // Number validations
  const numValue = typeof value === 'number' ? value : parseFloat(value)
  if (!isNaN(numValue)) {
    if (rules.min !== undefined && numValue < rules.min.value) {
      return {
        hasError: true,
        message: rules.min.message || `Valor mínimo: ${rules.min.value}`
      }
    }

    if (rules.max !== undefined && numValue > rules.max.value) {
      return {
        hasError: true,
        message: rules.max.message || `Valor máximo: ${rules.max.value}`
      }
    }
  }

  // Custom validations
  if (rules.custom) {
    for (const rule of rules.custom) {
      if (!rule.validate(value)) {
        return { hasError: true, message: rule.message }
      }
    }
  }

  return { hasError: false, message: '' }
}

export function useFormValidation<T extends Record<string, any>>(
  values: T,
  schema: ValidationSchema<T>
) {
  const [touched, setTouched] = useState<TouchedFields<T>>({})

  // Calculate errors for all fields
  const errors = useMemo(() => {
    const result: FormErrors<T> = {}
    for (const key in schema) {
      const rules = schema[key]
      if (rules) {
        result[key] = validateField(values[key], rules)
      }
    }
    return result
  }, [values, schema])

  // Get visible errors (only for touched fields)
  const visibleErrors = useMemo(() => {
    const result: FormErrors<T> = {}
    for (const key in errors) {
      if (touched[key]) {
        result[key] = errors[key]
      }
    }
    return result
  }, [errors, touched])

  // Mark field as touched
  const touch = useCallback((field: keyof T) => {
    setTouched(prev => ({ ...prev, [field]: true }))
  }, [])

  // Mark all fields as touched
  const touchAll = useCallback(() => {
    const allTouched: TouchedFields<T> = {}
    for (const key in schema) {
      allTouched[key] = true
    }
    setTouched(allTouched)
  }, [schema])

  // Reset touched state
  const resetTouched = useCallback(() => {
    setTouched({})
  }, [])

  // Check if form is valid
  const isValid = useMemo(() => {
    for (const key in errors) {
      if (errors[key]?.hasError) {
        return false
      }
    }
    return true
  }, [errors])

  // Get field props helper
  const getFieldProps = useCallback((field: keyof T) => {
    const error = visibleErrors[field]
    return {
      error: error?.hasError || false,
      errorMessage: error?.message || '',
      onBlur: () => touch(field),
    }
  }, [visibleErrors, touch])

  return {
    errors,
    visibleErrors,
    touched,
    touch,
    touchAll,
    resetTouched,
    isValid,
    getFieldProps,
  }
}

// Hook for single field validation
export function useFieldValidation(
  value: any,
  rules: FieldValidation,
  _validateOnChange = true
) {
  const [touched, setTouched] = useState(false)

  const error = useMemo(() => validateField(value, rules), [value, rules])

  const showError = touched && error.hasError

  const touch = useCallback(() => setTouched(true), [])
  const reset = useCallback(() => setTouched(false), [])

  return {
    error: showError,
    errorMessage: showError ? error.message : '',
    touched,
    touch,
    reset,
    isValid: !error.hasError,
  }
}
